import { useCallback, useMemo } from "react";
import {
  HISTORY_PAGE_SIZE,
  MAX_SUBGRAPH_ID,
  QUERY_CACHE_TIME,
  REQUEST_ID,
} from "@sablier/v2-constants";
import { _ } from "@sablier/v2-mixins";
import { Stream } from "@sablier/v2-models";
import { client, queries } from "@sablier/v2-subgraphs";
import { useInfiniteQuery } from "@tanstack/react-query";
import request from "graphql-request";

interface Props {
  id?: string;
  key?: string[];
  first?: number;
}

export default function useRequestStreamActions({
  id = "_",
  key = REQUEST_ID.streamActions,
  first = HISTORY_PAGE_SIZE,
}: Props) {
  const filter = useMemo(
    () =>
      _.toFilter({
        chainId: Stream.doSplitIdentifier(id).chainId,
      }),
    [id],
  );

  const endpoint = useMemo(
    () => client.getEndpointByChain({ chainId: filter.chainId }) || "",
    [filter],
  );

  const paginated = useCallback(
    async (first: number, subgraphId = MAX_SUBGRAPH_ID) => {
      const variables = {
        first,
        streamId: id,
        subgraphId,
      };
      return request(endpoint, queries.getActions_ByStream, variables);
    },
    [endpoint, id],
  );

  const {
    data,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery([...key, { unique: id }], () => paginated(first), {
    staleTime: QUERY_CACHE_TIME,
    cacheTime: QUERY_CACHE_TIME,
    getNextPageParam: (lastPage) => {
      return lastPage.actions.length === HISTORY_PAGE_SIZE
        ? lastPage.actions[lastPage.actions.length - 1].subgraphId
        : undefined;
    },
  });

  return useMemo(
    () => ({
      error,
      isLoading,
      data,
      hasNextPage,
      fetchNextPage,
      isFetchingNextPage,
    }),
    [data, error, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading],
  );
}
